import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useAuthStore, type Role } from '../stores/authStore';
import { trackJourneyStart, trackJourneyComplete, trackJourneyFailure } from '../observability/clarity';
import { createApiClient } from '../api/client';
import { Shield, Users, Receipt, User, BookOpen, ClipboardList, ArrowLeft, RotateCcw } from 'lucide-react';
import { LoginHero } from './LoginHero';

const api = createApiClient({ baseURL: (import.meta as any).env?.VITE_API_BASE_URL ?? '' });

type LoginStep = 'phone' | 'otp' | 'memberships';

interface MembershipOption {
  tenantId: string;
  role: Role;
  selectable: boolean;
}

function formatPhoneDisplay(phone: string): string {
  if (phone.startsWith('+977')) return `+977 ${phone.slice(4, 7)}-${phone.slice(7)}`;
  if (phone.startsWith('+91')) return `+91 ${phone.slice(3, 7)}-${phone.slice(7)}`;
  return phone;
}

export function LoginPage() {
  const [step, setStep] = useState<LoginStep>('phone');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [memberships, setMemberships] = useState<MembershipOption[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const sessionExpired = useAuthStore((s) => s.expired);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  function startResendCooldown() {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  async function requestOtp() {
    setError('');
    setLoading(true);
    trackJourneyStart('login', { source: 'login-page', step: 'phone' });
    try {
      await api.post('/auth/otp/request', { phoneE164: phone });
      setStep('otp');
      startResendCooldown();
      trackJourneyComplete('login', { step: 'otp-sent' });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
      if (axiosErr.response?.status === 429) {
        setError('Too many requests. Please wait a minute before trying again.');
      } else {
        setError(axiosErr.response?.data?.message ?? 'Failed to send OTP. Check your phone number.');
      }
      trackJourneyFailure('login', 'otp-request-failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestOtp(e: React.FormEvent) {
    e.preventDefault();
    await requestOtp();
  }

  async function handleVerifyOtp(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    trackJourneyStart('login', { source: 'login-page', step: 'otp-verify' });
    try {
      await api.post('/auth/otp/verify', { phoneE164: phone, code: otpCode });
      // msk_idsession cookie is now set. Fetch memberships.
      const res = await api.get('/auth/memberships');
      const options: MembershipOption[] = res.data;

      if (options.length === 0) {
        setError('No accounts found for this phone number. Contact your school admin.');
        setStep('phone');
        return;
      }

      const selectable = options.filter((m) => m.selectable);
      if (selectable.length === 0) {
        setError('Your school account is not active. Contact your school admin.');
        setStep('phone');
        return;
      }

      if (selectable.length === 1) {
        // Auto-select the single membership
        const first = selectable[0];
        if (first) await selectMembership(first);
        return;
      }

      // Multiple memberships — show selector
      setMemberships(selectable);
      setStep('memberships');
      trackJourneyComplete('login', { step: 'otp-verified', memberships: String(selectable.length) });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
      if (axiosErr.response?.status === 401) {
        setError('Invalid or expired code. Please try again.');
      } else if (axiosErr.response?.status === 429) {
        setError('Too many verification attempts. Please wait before trying again.');
      } else {
        setError(axiosErr.response?.data?.message ?? 'Verification failed. Please try again.');
      }
      trackJourneyFailure('login', 'otp-verify-failed');
    } finally {
      setLoading(false);
    }
  }

  async function selectMembership(m: MembershipOption) {
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/select-membership', { tenantId: m.tenantId, role: m.role });
      // JWT + CSRF + refresh cookies are now set. Build the session.
      setSession({
        userId: m.tenantId,
        roles: [m.role],
        activeRole: m.role,
        children: [],
        activeChildId: null,
        schoolDefaultLocale: 'en',
      });
      trackJourneyComplete('login', { step: 'membership-selected', role: m.role });
      const target = roleLanding(m.role);
      navigate(target, { replace: true });
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { code?: string; message?: string } } };
      setError(axiosErr.response?.data?.message ?? 'Failed to sign in. Please try again.');
      trackJourneyFailure('login', 'membership-select-failed');
    } finally {
      setLoading(false);
    }
  }

  function roleLanding(role: Role): string {
    switch (role) {
      case 'SchoolAdmin': return '/dashboard';
      case 'Teacher': return '/today';
      case 'Accountant': return '/collect';
      case 'Parent': return '/home';
      case 'Student': return '/today';
      case 'ExamCoordinator': return '/marks';
      default: return '/workspace';
    }
  }

  function handleResend() {
    if (resendCooldown > 0) return;
    void requestOtp();
  }

  return (
    <div className="login-landing">
      <LoginHero />

      <aside className="login-form-col">
        <div className="login-auth-card">
          <div className="login-auth-head">
            <p className="login-auth-head-kicker">Secure entry</p>
            <h2>Use your phone number to continue.</h2>
            <p>We’ll send a verification code, then route you to the correct school workspace.</p>
          </div>
        {sessionExpired && (
          <div className="banner banner-warn mb-4" role="alert">
            Your session expired. Please sign in again — any unsynced attendance or marks are saved and will sync once you're back in.
          </div>
        )}

        {step === 'phone' && (
          <form onSubmit={handleRequestOtp}>
            <div className="form-group">
              <label className="form-label" htmlFor="phone">Phone number</label>
              <input
                id="phone"
                type="tel"
                className="form-input"
                placeholder="+97798XXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                pattern="^\+\d{10,15}$"
                autoComplete="tel"
                data-clarity-mask="true"
              />
              <p className="text-hint">
                We'll send a verification code via SMS
              </p>
            </div>
            {error && <div className="banner banner-danger" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || !phone}>
              {loading ? 'Sending code…' : 'Send verification code'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={handleVerifyOtp}>
            <p className="text-body-sm mb-4">
              Enter the 6-digit code sent to <strong>{formatPhoneDisplay(phone)}</strong>
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="otp">Verification code</label>
              <input
                id="otp"
                type="text"
                inputMode="numeric"
                className="form-input"
                placeholder="123456"
                value={otpCode}
                onChange={(e) => { if (/^\d{0,6}$/.test(e.target.value)) setOtpCode(e.target.value); }}
                required
                pattern="^\d{6}$"
                maxLength={6}
                autoComplete="one-time-code"
                data-clarity-mask="true"
                autoFocus
              />
            </div>
            {error && <div className="banner banner-danger" role="alert">{error}</div>}
            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading || otpCode.length !== 6}>
              {loading ? 'Verifying…' : 'Verify code'}
            </button>
            <div className="flex-center-gap3 mt-3">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { setStep('phone'); setError(''); setOtpCode(''); }}
              >
                <ArrowLeft size={14} aria-hidden="true" />
                Change number
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={handleResend}
                disabled={resendCooldown > 0}
              >
                <RotateCcw size={14} aria-hidden="true" />
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {step === 'memberships' && (
          <div>
            <p className="text-body-sm mb-4">
              Choose which account to sign in as:
            </p>
            {error && <div className="banner banner-danger" role="alert">{error}</div>}
            <div className="flex-col-gap2">
              {memberships.map((m) => (
                <button
                  key={`${m.tenantId}-${m.role}`}
                  className="btn btn-secondary btn-lg btn-left"
                  onClick={() => selectMembership(m)}
                  disabled={loading}
                >
                  <RoleIcon role={m.role} />
                  <span>{m.role}</span>
                </button>
              ))}
            </div>
            <div className="mt-3 text-center">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => { setStep('phone'); setError(''); setOtpCode(''); }}
              >
                <ArrowLeft size={14} aria-hidden="true" />
                Sign in with different number
              </button>
            </div>
          </div>
        )}

        <hr className="public-divider" />

        <div className="public-footnote">
          <Link to="/demo">Try demo mode</Link>
        </div>

        <div className="public-footnote mt-2">
          Don't have an account? <Link to="/signup">Start free trial</Link>
          <span className="footnote-sep">·</span>
          <Link to="/pricing">View pricing</Link>
        </div>
        </div>
      </aside>
    </div>
  );
}

function RoleIcon({ role }: { role: Role }) {
  const iconProps = { size: 18, 'aria-hidden': true as const };
  switch (role) {
    case 'SchoolAdmin': return <Shield {...iconProps} />;
    case 'Teacher': return <Users {...iconProps} />;
    case 'Accountant': return <Receipt {...iconProps} />;
    case 'Parent': return <User {...iconProps} />;
    case 'Student': return <BookOpen {...iconProps} />;
    case 'ExamCoordinator': return <ClipboardList {...iconProps} />;
    default: return <User {...iconProps} />;
  }
}
